/**
 * Bell con badge de notifs no leídas + popover con las últimas 5.
 * Sprint 9.2.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Bell, CheckCheck, MessageSquare, Calendar, Star, Award, Inbox, LifeBuoy, Heart, AtSign } from 'lucide-react';
import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type NotificationRow,
  type NotificationKind,
} from '@/lib/api/notifications';
import { cn } from '@/lib/utils';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notifs } = useNotifications({ limit: 6 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const location = useLocation();

  // El feed completo está registrado en /dashboard/notifications,
  // /guru/notifications y /admin/notifications (mismo componente).
  // Apuntamos al que matchea el contexto donde estamos parados.
  const seeAllUrl = location.pathname.startsWith('/admin')
    ? '/admin/notifications'
    : location.pathname.startsWith('/guru')
      ? '/guru/notifications'
      : '/dashboard/notifications';

  // Cerrar al click afuera
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function onClickNotif(n: NotificationRow) {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.cta_url) {
      setOpen(false);
      navigate(n.cta_url);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-[#10232B] transition-colors hover:bg-[#EEF7F8] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-accent"
        aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            style={{ lineHeight: '1' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-4 top-[88px] z-50 rounded-xl border bg-white shadow-elevated md:absolute md:inset-x-auto md:right-0 md:top-[calc(100%+8px)] md:w-[360px]"
          style={{ borderColor: '#DCE7EC' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#DCE7EC' }}>
            <p className="font-semibold text-sm">Notificaciones</p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="inline-flex items-center gap-1 text-xs text-brand-accent hover:underline"
                disabled={markAllRead.isPending}
              >
                <CheckCheck className="h-3 w-3" />
                Marcar todas leídas
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {!notifs ? (
              <p className="py-8 text-center text-xs text-muted-foreground">Cargando…</p>
            ) : notifs.length === 0 ? (
              <div className="py-10 text-center">
                <Inbox className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-2 text-sm text-muted-foreground">Sin notificaciones</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: '#DCE7EC' }}>
                {notifs.slice(0, 5).map((n) => (
                  <NotifRow key={n.id} n={n} onClick={onClickNotif} />
                ))}
              </div>
            )}
          </div>

          <Link
            to={seeAllUrl}
            onClick={() => setOpen(false)}
            className="block px-4 py-3 text-center text-xs font-medium text-brand-accent hover:bg-[#EEF7F8] border-t"
            style={{ borderColor: '#DCE7EC' }}
          >
            Ver todas
          </Link>
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, onClick }: {
  n: NotificationRow;
  onClick: (n: NotificationRow) => void;
}) {
  const Icon = iconForKind(n.kind);
  const ago = timeAgo(n.created_at);

  return (
    <button
      type="button"
      onClick={() => onClick(n)}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F6FAFB]',
        !n.is_read && 'bg-brand-accent/[0.03]'
      )}
    >
      <div className={cn(
        'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
        n.is_read ? 'bg-muted text-muted-foreground' : 'bg-brand-accent/10 text-brand-accent'
      )}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm leading-snug',
          n.is_read ? 'text-muted-foreground' : 'font-medium text-foreground'
        )}>
          {n.title}
        </p>
        {n.body && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.body}</p>
        )}
        <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground/60">
          {ago}
        </p>
      </div>
      {!n.is_read && (
        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-brand-accent" />
      )}
    </button>
  );
}

export function iconForKind(kind: NotificationKind) {
  switch (kind) {
    case 'booking_confirmed':
    case 'booking_cancelled':
    case 'session_reminder_24h':
    case 'session_reminder_1h':
    case 'session_completed':
    case 'event_starting':
      return Calendar;
    case 'new_review':
      return Star;
    case 'community_reply':
      return MessageSquare;
    case 'community_reaction':
      return Heart;
    case 'community_mention':
      return AtSign;
    case 'support_ticket_new':
    case 'support_ticket_reply':
      return LifeBuoy;
    case 'new_settlement':
      return Award;
    case 'system':
    default:
      return Bell;
  }
}

export function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'recién';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `hace ${diffD}d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}
